import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColumnState, RowNode } from 'ag-grid-community';

import { EGuaranteesGridPaths } from '@ruby/shared/enums/guarantees.enum';
import { IGridConfigurations } from '@ruby/shared/models/commons/grid.interface';
import { IGuarantee } from '@ruby/shared/models/guarantees/guarantees.interface';

@Component({
  selector: 'ng-guarantees-grid',
  templateUrl: './guarantees-grid.component.html',
  styleUrls: ['./guarantees-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GuaranteesGridComponent implements OnChanges {
  @Input() loading!: boolean;
  @Input() configs!: IGridConfigurations;
  @Input() data: Array<IGuarantee> = [];
  @Input() invalidEquipments: Array<string> = [];
  @Input() type!: EGuaranteesGridPaths;
  @Input() activeEquipments: Array<string> = [];
  @Output() refresh = new EventEmitter<void>();
  grid!: AgGridAngular;
  columnState: Array<ColumnState> = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes.activeEquipments?.currentValue) {
      this.activeEquipments = changes.activeEquipments.currentValue;
    }
  }

  /**
   * Handle Refresh
   *
   * @summary Emits refresh action to refresh the grid
   * @returns void
   */
  handleRefresh(): void {
    this.refresh.emit();
  }

  /**
   * Emit Grid Ready
   *
   * @summary Emits a grid reference when the grid is ready for most api calls
   * @param $event AgGridAngular
   * @returns void
   */
  emitGridReady($event: AgGridAngular): void {
    this.grid = $event;
  }

  /**
   * Emit Sort
   *
   * @summary Emit a sort event when the user sorts the data
   * @returns void
   */
  emitSort(): void {
    this.columnState = [...this.grid.columnApi.getColumnState()];
  }

  /**
   * Emit Row Data Updated
   *
   * @summary Emit row data updated when the data is updated
   * @returns void
   */
  emitRowDataUpdated(): void {
    if (this.columnState.length > 0) {
      this.grid.columnApi.applyColumnState({ state: this.columnState, applyOrder: true });
    }
    if (this.activeEquipments.length > 0) {
      this.grid.api.forEachNode((node: RowNode) => {
        const { equipmentId } = node.data;
        if (this.activeEquipments.indexOf(equipmentId) !== -1) {
          const rowIndex = node.rowIndex || 0;
          const nodeId = node.id || '';
          this.grid.api.ensureIndexVisible(rowIndex);
          this.grid.api.getRowNode(nodeId)?.setSelected(true, true);
        }
      });
    }
  }
}
